import React, { useCallback, useEffect, useState, useContext } from 'react';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { Accordion } from '@jutro/legacy/components';
import { FlowStepId, PAConstants } from 'wmic-pe-portals-utils-js';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import {
    WMICDriverEndorsementsComponent,
    WMICPAVehicleCoverages,
    WMICPAPolicyCoverages,
} from 'wmic-pe-capability-gateway-common-pa-react';
import WMICPACoverageUtil from 'wmic-pe-capability-gateway-common-pa-react/util/WMICPACoverageUtil';
import PropTypes from 'prop-types';

import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import _ from 'lodash';
import appConfig from 'app-config';
import {
    useAccordionValidation,
    useDocumentTitle,
    useWizardModals,
    WMICWizardSubmissionPage,
} from 'wmic-pe-portals-wizard-components-ui';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import styles from './WMICPACoveragesPage.module.scss';

import messages from './WMICPACoveragesPage.messages';
import metadata from './WMICPACoveragesPage.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

const structureCustomQuote = (submissionVM, affectedQuote, clauses) =>
    // convert OfferingDTO to CustomQuotedDTO structure
    ({
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
    });
const generateColumnData = submissionVM => {
    const lobOfferingPath = 'lobData.personalAuto.offerings';

    const lobOfferings = _.get(submissionVM, `${lobOfferingPath}.value`);

    const columnData = lobOfferings.map((lobOffering, lobIndex) => ({
        name: lobOffering.branchName,
        code: lobOffering.branchCode,
        lob: {
            path: `${lobOfferingPath}.children[${lobIndex}]`,
            data: lobOffering,
        },
    }));
    return _.sortBy(columnData, ['code']);
};

const getCustomQuote = (vm, lobPath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages,
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(lobOffering.coverages, lobName, null);
    }

    return structureCustomQuote(vm, lobOffering, clausesToUpdate);
};

function WMICPACoveragesPage(props) {
    const { wizardData: submissionVM, updateWizardData, underwritingIssues } = props;
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);

    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading } = useWizardModals();
    const { authHeader } = useAuthentication();
    const { onValidate, disregardFieldValidation, initialValidation, isComponentValid } =
        useValidation('paCoveragesPage');
    const { onValidateAccordion, isAccordionValid } = useAccordionValidation(onValidate);
    const viewModelService = useContext(ViewModelServiceContext);
    const [accordionColumnData, setAccordionColumnData] = useState({});

    useDocumentTitle(translator(messages.wmicPaCoveragesTitle), submissionVM);

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.PA_COVERAGES;
    }, [submissionVM.value]);

    const onClauseChange = useCallback(
        _basePath => {
            const lobPath = 'lobData.personalAuto.offerings.children[0]';
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, lobName, true);
            return CustomQuoteService.updateCustomQuoteCoveragesAndQuoteData(
                customQuote,
                submissionVM.value,
                authHeader
            ).then(response => {
                const newSubmissionVM = viewModelService.clone(submissionVM);
                newSubmissionVM.value = response;

                const removedFieldsFromBaseCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.lineCoverages`
                );
                const removedFieldsFromAdditionalCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.vehicleCoverages`
                );
                const removedFieldsFromDriverEndorsementCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.driverEndorsements_WMIC`
                );
                const allRemovedFields = [
                    ...removedFieldsFromBaseCoverages,
                    ...removedFieldsFromAdditionalCoverages,
                    ...removedFieldsFromDriverEndorsementCoverages,
                ];
                disregardFieldValidation(allRemovedFields);
                updateWizardData(newSubmissionVM);
            });
        },
        [submissionVM, CustomQuoteService, authHeader, viewModelService, disregardFieldValidation, updateWizardData]
    );

    const columnData = generateColumnData(submissionVM);

    useEffect(() => {
        if (columnData.length === 0) {
            const updatedColumnData = generateColumnData(submissionVM);
            setAccordionColumnData(updatedColumnData);
        }
    }, []);

    const syncCoverages = useCallback(
        (value, changedPath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onClauseChange(basePath);
        },
        [onClauseChange]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath);
        },
        [changeSubmission, syncCoverages]
    );

    // props for the accordions themselves
    const commonAccordionProps = {};

    // props for the accordion content components
    const commonAccordionContentProps = {
        baseData: _.get(submissionVM, 'baseData'),
        authHeader,
        submissionVM,
        updateWizardData,
        onClauseChange,
        syncCoverages,
        changeSubmissionAndSync,
        onValidate: onValidateAccordion,
    };

    const getThirdPartyLiabilityWarning = () => [
        {
            id: 'warningThirdPartyLiabilityContainer',
            type: 'element',
            component: 'div',
            content: [
                {
                    id: 'exclamationcircleIcon',
                    type: 'element',
                    component: 'Icon',
                    componentProps: {
                        icon: 'mi-error',
                        className: 'exclamation-icon gw-mr-1 iconAlignment',
                    },
                },
                {
                    id: 'warningThirdPartyLiabilityMessage',
                    type: 'element',
                    component: 'span',
                    content: {
                        id: 'quoteandbind.pa.views.pa-coverage-term.Third Party Liability Applies to All vehicles',
                        defaultMessage: translator(messages.thirdPartyLiabilityWarning),
                    },
                },
            ],
        },
    ];

    const getCustomTerms = clause => {
        if (PAConstants.thirdPartyLiabililityLimitNames.includes(clause.publicID)) {
            return getThirdPartyLiabilityWarning();
        }
        return null;
    };

    const commonOverrides = {
        coverageColumnData: columnData,
        quoteID: submissionVM.quoteID.value,
        changeSubmissionAndSync,
        changeSubmission,
        onClauseChange,
        syncCoverages,
        underwritingIssues,
        onValidate,
        getCustomTerms,
        showErrors,
    };

    const getOrderedVehiclesList = () => {
        const vehicles = _.get(submissionVM, `${PAConstants.PACoveragesPaths.VEHICLES_PATH}.children`, []);
        return _.sortBy(vehicles, ['vehicleNumber_WMIC.value']);
    };

    const _iterableProps = () => {
        const vehicleOverrides = getOrderedVehiclesList().map((vehicle, index) => {
            const vehicleCoverages = WMICPACoverageUtil.getVehicleCoveragesForRisk(
                _.get(submissionVM, 'lobData.personalAuto'),
                vehicle.publicID.value
            );
            const vehicleOverride = {
                [`vehicleCoveragesIterableComponent${index}`]: {
                    vehicleVM: vehicle,
                    vehicleIndex: index,
                    baseData: submissionVM.baseData,
                    vehicleCoverages,
                    jobVM: submissionVM,
                    ...commonOverrides,
                },
            };

            return { ...vehicleOverride };
        });

        return Object.assign({}, ...vehicleOverrides);
    };

    const overrideProps = {
        vehicleCoveragesIterableAccordionContainer: {
            data: getOrderedVehiclesList(),
        },
        policyCoveragesContainer: {
            jobVM: submissionVM,
            lineCoverages: _.get(submissionVM, 'lobData.personalAuto.offerings.children[0].coverages.lineCoverages'),
            ...commonOverrides,
        },
        driverEndorsementsAccordion: {
            ...commonAccordionProps,
            isValid: isAccordionValid('driverEndorsementsComponent'),
        },
        driverEndorsementsComponent: {
            accountHolder: _.get(submissionVM, 'baseData.accountHolder.value'),
            jobVM: submissionVM,
            ...commonAccordionContentProps,
        },
        ..._iterableProps(),
    };

    const onNext = useCallback(async () => {
        try {
            if (!isComponentValid) {
                setShowErrors(true);
                return false;
            }
            setWizardLoading(true);
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmissionWithLOBData(
                newSubmissionVM.value,
                authHeader
            );
            updateWizardData(submissionVM);
            return submissionVM;
        } finally {
            setWizardLoading(false);
        }
    }, [LoadSaveService, authHeader, isComponentValid, setWizardLoading, submissionVM, viewModelService]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onChangeSubmission: changeSubmission,
            onSyncCoverages: syncCoverages,
        },
        resolveComponentMap: {
            WMICDriverEndorsementsComponent,
            WMICPAVehicleCoverages,
            WMICPAPolicyCoverages,
            Accordion,
        },
    };

    return (
        <WMICWizardSubmissionPage
            skipWhen={initialValidation}
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            flowStepId={FlowStepId.PA_COVERAGES}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPACoveragesPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    ...wizardProps,
};

export default WMICPACoveragesPage;
